// ============================================================================
// SpectatorBanner - fixed banner shown when viewing a table in spectator mode
// ============================================================================
// Mounts on GamePage when ?spectate=1. Persists across the page (non-
// dismissible by design - spectators should always know they're observing,
// not playing). Surfaces:
//   - "← Back to my table" jump if the user has another table they're
//     seated at (so they don't lose their game while spectating someone)
//   - "Leave" fallback that drops them into the tournament lobby or
//     browser-back if it's a cash spectate
// ============================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, HStack, Text, Button } from '@chakra-ui/react';
import { Eye, LogOut, ArrowLeft } from 'lucide-react';
import { useMySeatedTables } from '../../hooks/useMySeatedTables';

interface SpectatorBannerProps {
  visible: boolean;
  tournamentId?: string;
  currentUserId?: string | null;
  // tableId of the table we're CURRENTLY spectating - used to filter out
  // self when picking the "back to my table" target.
  spectatingTableId?: string;
  onLeave?: () => void;
}

const SpectatorBanner: React.FC<SpectatorBannerProps> = ({
  visible, tournamentId, currentUserId, spectatingTableId, onLeave,
}) => {
  const navigate = useNavigate();
  // Hook order must be stable - call before any early return.
  const mySeated = useMySeatedTables(currentUserId || null);
  const backTable = mySeated.find(t => t.tableId !== spectatingTableId);

  if (!visible) return null;

  const handleLeave = () => {
    // Explicit leave: terminate the spectator session and route away.
    if (onLeave) onLeave();
    if (tournamentId) {
      navigate('/tournaments');
    } else {
      navigate(-1);
    }
  };

  const handleBackToMyTable = () => {
    // Do NOT terminate the spectator session - the spectated table stays
    // in the dock as a watching session to hop back to. FULL page load:
    // GamePage-to-GamePage route swaps render the OLD table's state under
    // the new URL, so spectate hops always hard-navigate.
    if (backTable) window.location.assign(`/game/${backTable.tableId}`);
  };

  return (
    <Box
      position="fixed"
      top={0}
      left="50%"
      transform="translateX(-50%)"
      zIndex={8500}
      mt={2}
      bg="rgba(6, 182, 212, 0.95)"
      color="white"
      borderRadius="full"
      px={4}
      py={2}
      boxShadow="0 4px 16px rgba(0,0,0,0.4)"
      backdropFilter="blur(4px)"
    >
      <HStack spacing={3}>
        <Eye size={16} />
        <Text fontSize="sm" fontWeight="bold" letterSpacing="wide">
          Spectating
        </Text>

        {/* Primary action when the user has their own seated table: jump
            back. Bright contrast button so it's the obvious choice. */}
        {backTable && (
          <Button
            size="xs"
            bg="white"
            color="cyan.700"
            fontWeight="bold"
            _hover={{ bg: 'gray.100' }}
            leftIcon={<ArrowLeft size={12} />}
            onClick={handleBackToMyTable}
          >
            {backTable.myTurn ? 'Back · YOUR TURN' : 'Back to my table'}
          </Button>
        )}

        {/* Secondary: leave to lobby */}
        <Button
          size="xs"
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.15)' }}
          leftIcon={<LogOut size={12} />}
          onClick={handleLeave}
        >
          Leave
        </Button>
      </HStack>
    </Box>
  );
};

export default SpectatorBanner;
